import React, { createContext, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Image } from 'antd';

import { hasAttachmentVariant } from '../../utils/attachmentVariants';
import { filesOf, showsAsPicture } from '../../utils/fileValues';
import {
	publicFileDownloadUrl,
	publicDownloadNote,
	publicFileVariantUrl,
	signInToDownloadPath,
	visitorMayDownload,
} from '../../utils/publicFiles';
import { DOWNLOAD } from '../icons';
import { FileChip } from './FileChip';
import { EXTERNAL_LINK_PROPS } from './ExternalLink';

/**
 * Files on a PUBLIC page (PR 10c; the 10c contract, owner rulings G13–G15).
 *
 * The shared page wraps its body in <PublicFilesProvider code>, and the file
 * surfaces the app already has (components/common/FileValue.js — the read
 * view, the tile, the cell) ask `usePublicFiles()` and, inside one, draw from
 * the public facts instead of the signed-in routes:
 *
 *   - a tile draws the payload's inline `thumb` (no byte request; none, the
 *     chip);
 *   - the read view shows a photo's `md` and opens its `lg` full screen;
 *   - Download is a plain link to the public download route for a file the
 *     API marked `download: 'original'`, and a `'signin'` file shows its name
 *     with "Sign in to download" — the sign-in page, back to this page after;
 *   - the malware gate (PR 10e): a file whose verdict is on its way
 *     (`'checking'`) says "Checking this file. Try again in a minute.", and
 *     a flagged one (`'unavailable'`) "This file isn't available." — its
 *     name and its pictures stay (re-encoded pixels), its bytes do not.
 *
 * Every byte is fetched by an <img> or an <a>: no Authorization header, so a
 * visitor is a visitor whoever is signed in on the machine. The uploader is
 * never shown (the payload does not carry one). Only the shared page provides
 * this context; everywhere else `usePublicFiles()` is null.
 */
const PublicFilesContext = createContext(null);

export function PublicFilesProvider({ code, children }) {
	const value = useMemo(() => (code ? { code } : null), [code]);
	return <PublicFilesContext.Provider value={value}>{children}</PublicFilesContext.Provider>;
}

export const usePublicFiles = () => useContext(PublicFilesContext);

/** One public file as a picture: `sm` from the inline thumb, `md` from its route. */
export function PublicFilePicture({ code, file, size, className = '', chipSize = 'sm' }) {
	const [broken, setBroken] = useState(false);
	const src = size === 'sm' ? file?.thumb : publicFileVariantUrl(code, file?._id, size);
	if (!showsAsPicture(file, size) || !src || broken) return <FileChip file={file} size={chipSize} />;
	return (
		<span className={`file-picture file-picture--${size}${className ? ` ${className}` : ''}`} data-testid="file-picture">
			<img
				className="file-picture__img"
				src={src}
				alt={file?.originalName || ''}
				draggable={false}
				onError={() => setBroken(true)}
			/>
		</span>
	);
}

/**
 * Download for a visitor, "Sign in to download" for a file they may not take
 * (G15), or — the malware gate, PR 10e — one plain line for a file still
 * being checked or flagged: no door, nothing to click.
 */
export function PublicFileDownload({ code, file, compact = false }) {
	const name = file?.originalName || 'file';
	const note = publicDownloadNote(file);
	if (note) {
		return <span className={`public-file__note public-file__note--${file.download}`}>{note}</span>;
	}
	if (!visitorMayDownload(file)) {
		return (
			<Link className="public-file__signin" to={signInToDownloadPath(code)}>
				Sign in to download
			</Link>
		);
	}
	return (
		<Button
			className="file-value__download"
			size="small"
			type={compact ? 'text' : 'default'}
			icon={<DOWNLOAD />}
			href={publicFileDownloadUrl(code, file?._id)}
			// A new tab: a download saves and the tab closes; a refusal (the
			// link revoked since, the day's file budget spent) shows there
			// instead of replacing the page the visitor is reading.
			{...EXTERNAL_LINK_PROPS}
			aria-label={`Download ${name}`}
		>
			{compact ? null : 'Download'}
		</Button>
	);
}

/**
 * The full-screen `lg` of one public photo (antd's Image preview, zoom and
 * pan), open while `file` is set. Nothing is fetched until it opens.
 */
export function PublicPhotoViewer({ code, file, onClose }) {
	if (!file) return null;
	const src = publicFileVariantUrl(code, file._id, 'lg');
	return (
		<Image
			rootClassName="public-file__viewer"
			style={{ display: 'none' }}
			src={src}
			alt={file.originalName || ''}
			preview={{ visible: true, src, onVisibleChange: (visible) => { if (!visible) onClose(); } }}
		/>
	);
}

/** Opens full screen only with the `lg` made. */
export const opensFullScreen = file => showsAsPicture(file, 'md') && hasAttachmentVariant(file, 'lg');

/**
 * A file element's value on a public item page: each photo its `md` (a click
 * or a tap opens the `lg` full screen) with its download under it, each
 * document its chip with its size and its download beside it. An id with no
 * facts shows nothing.
 */
export function PublicFileValue({ code, item, value }) {
	const files = filesOf(item, value);
	const [viewing, setViewing] = useState(null);
	if (!files.length) return null;
	return (
		<div className="file-value file-value--public" data-testid="file-value">
			{files.map((file) => {
				const name = file.originalName || 'photo';
				if (showsAsPicture(file, 'md')) {
					const picture = <PublicFilePicture code={code} file={file} size="md" chipSize="md" />;
					return (
						<div key={file._id} className="public-file__photo">
							{opensFullScreen(file) ? (
								<button
									type="button"
									className="file-value__photo"
									aria-label={`Open ${name}`}
									onClick={() => setViewing(file)}
								>
									{picture}
								</button>
							) : <div className="file-value__photo">{picture}</div>}
							<PublicFileDownload code={code} file={file} />
						</div>
					);
				}
				return (
					<div key={file._id} className="file-value__doc">
						<FileChip file={file} showSize />
						<PublicFileDownload code={code} file={file} />
					</div>
				);
			})}
			<PublicPhotoViewer code={code} file={viewing} onClose={() => setViewing(null)} />
		</div>
	);
}
